import { useTranslation } from "react-i18next";
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { ToggleSurface } from "../../../common/core/Surface";
import { Defaults, useEntityField } from "@emberly/zenith-client";
import { makeStyles } from "tss-react/mui";
import { MissionFormEnums } from "../../../../common/constants";
import ContactPicker from "../../../common/inputs/ContactPicker";

const useStyles = makeStyles()(
  (theme) => ({
    subheader: {
      marginTop: theme.spacing(1)
    },
    checkboxGroup: {
      marginLeft: theme.spacing(1.5)
    },
    formTypeCard: {
      position: "relative",
      padding: theme.spacing(1.5),
      width: "100%",
      minHeight: "100%",
      background: theme.palette.background.grey,
      borderRadius: theme.spacing(1.5),
      cursor: "default",
      userSelect: "none",
      "& img": {
        borderRadius: theme.spacing(1),
        height: "144px",
        width: "100%",
        objectFit: "cover"
      },
      "&.selected": {
        outline: `5px solid ${theme.palette.secondary.main}`,

      },
      "&:hover": {
        "&:after": {
          zIndex: 100,
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          background: theme.palette.action.hover,
          borderRadius: theme.spacing(1.5),
          content: "''",
          display: "block",
          pointerEvents: "none"
        },
      },
      "&.disabled": {
        "&:after": {
          zIndex: 100,
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          background: theme.palette.common.white,
          opacity: 0.25,
          borderRadius: theme.spacing(1.5),
          content: "''",
          display: "block",
          pointerEvents: "none"
        },
      }
    },
  })
);


const MAX_LENGTH_160 = { input: { inputProps: { maxLength: 160 } } };
const MAX_LENGTH_320 = { input: { inputProps: { maxLength: 320 } } };

export default function MissionFormSettingsCard() {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const name = useEntityField("name", Defaults.String);
  const formType = useEntityField("formType", Defaults.Enum);
  const isAdvanced = formType.value === MissionFormEnums.FormType.AdvancedForm;


  const header = useEntityField("header", Defaults.String);
  const message = useEntityField("message", Defaults.String);

  const enableSalvageMissions = useEntityField("enableSalvageMissions", Defaults.Boolean);
  const enableTowingMissions = useEntityField("enableTowingMissions", Defaults.Boolean);
  const enableOtherMissions = useEntityField("enableOtherMissions", Defaults.Boolean);

  const customer = useEntityField("customer", Defaults.NamedReference);

  return (
    <ToggleSurface title={t("missionForm:settings")} md={12} lg={12}>
      <Grid item container spacing={1.5}>

        <Grid item xs={12}>
          <TextField
            label={t("missionForm:name")}
            variant="filled"
            fullWidth
            size="small"
            value={name.value}
            onChange={name.onChange}
            slotProps={MAX_LENGTH_160}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.subheader}>{t("missionForm:adjustForm")}</Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label={t("missionForm:header")}
            variant="filled"
            fullWidth
            size="small"
            value={header.value}
            onChange={header.onChange}
            slotProps={MAX_LENGTH_160}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label={t("missionForm:message")}
            variant="filled"
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={message.value}
            onChange={message.onChange}
            slotProps={MAX_LENGTH_320}
          />
        </Grid>


        <Grid item xs={12}>
          <FormControl>
            <FormLabel>
              <Typography variant="body1" color="textPrimary">
                {t("missionForm:selectMissionTypes")}
              </Typography>
            </FormLabel>
            <FormGroup className={classes.checkboxGroup}>
              <FormControlLabel
                label={t("mission:enums:type:Salvage")}
                control={
                  <Checkbox
                    checked={enableSalvageMissions.value}
                    onChange={() => enableSalvageMissions.onChange(null, !enableSalvageMissions.value)}
                  />
                }
              />
              <FormControlLabel
                label={t("mission:enums:type:Towing")}
                control={
                  <Checkbox
                    checked={enableTowingMissions.value}
                    onChange={() => enableTowingMissions.onChange(null, !enableTowingMissions.value)}
                  />
                }
              />
              <FormControlLabel
                label={t("mission:enums:type:Other")}
                control={
                  <Checkbox
                    checked={enableOtherMissions.value}
                    onChange={() => enableOtherMissions.onChange(null, !enableOtherMissions.value)}
                  />
                }
              />

            </FormGroup>
          </FormControl>
        </Grid>


        <Grid item xs={12}>
          <ContactPicker
            label={t("missionForm:customer")}
            onChange={(_, data) => customer.onChange(null, !!data ? { id: data.id, name: data.name } : null)}
            value={customer.value}
            initialValues={customer.value}
            customer
          />
        </Grid>

      </Grid>
    </ToggleSurface>
  );
}


function FormTypeCard(props) {
  const { classes } = useStyles();
  const { title, description, imageSrc, selected, onClick, disabled } = props;


  return (
    <Box className={`${classes.formTypeCard} ${selected ? "selected" : ""} ${disabled ? "disabled" : ""}`} onClick={disabled ? undefined : onClick}>
      <Stack spacing={0.5}>

        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="body2" color="textSecondary">{description}</Typography>

        <img
          src={imageSrc}
        ></img>

      </Stack>
    </Box>
  )
}
